/**
 * Noticing that a new version has shipped.
 *
 * The service worker was registered and then forgotten. That is fine in a
 * browser tab, where a reload fetches a fresh `index.html` and with it the new
 * content-hashed bundle. It is not fine for a Home Screen install: iOS suspends
 * the web view and restores it, JavaScript context intact, without ever
 * re-fetching. The old bundle then runs for as long as the app is never
 * force-quit — so a deploy can succeed, the server can serve the new build, and
 * the person holding the phone still sees last week's app with no indication
 * that anything happened.
 *
 * The service worker already calls `skipWaiting()` and `clients.claim()`, so a
 * new one takes control the moment it installs. `controllerchange` is therefore
 * the honest signal: from that point the running page's JavaScript is older
 * than the cache serving it.
 */

let pending = false;
let reloading = false;
const listeners = new Set<() => void>();

/** Subscribe to "a newer version is now available". */
export function onUpdateReady(fn: () => void): () => void {
  listeners.add(fn);
  if (pending) fn();
  return () => listeners.delete(fn);
}

export function isUpdatePending(): boolean {
  return pending;
}

/** Reload onto the new build. Guarded, so a burst of events cannot loop. */
export function applyUpdate(): void {
  if (reloading) return;
  reloading = true;
  window.location.reload();
}

function announce(): void {
  if (pending) return;
  pending = true;
  for (const fn of listeners) fn();
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.register('/sw.js').catch((err: Error) => {
    // Not fatal: the app works online without it.
    console.warn('offline shell unavailable:', err.message);
  });

  // A worker took over a page that already had one. The first registration of
  // a session has no previous controller and is not an update.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (navigator.serviceWorker.controller) announce();
  });

  // Ask, rather than wait to be told. iOS checks for a new worker on its own
  // schedule, and a suspended app that is resumed may not check at all — which
  // is the case this exists for.
  const check = () => { void navigator.serviceWorker.getRegistration().then((r) => r?.update()); };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    // Coming back to a backgrounded app is the one moment nothing is half
    // typed, so an update waiting here is applied rather than offered.
    if (pending) applyUpdate();
    else check();
  });
}
